interface LogEntry {
  id: string;
  timestamp: number;
  datetime: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  data?: unknown;
  stack?: string;
  group?: string;
  tags?: string[];
  duration?: number;
  searchableText?: string;
  userAgent?: string;
  url?: string;
  memoryUsage?: {
    used: number;
    total: number;
    limit: number;
  };
  performance?: {
    navigationStart: number;
    loadEventEnd: number;
    domContentLoaded: number;
  };
  context?: {
    userId?: string;
    sessionId?: string;
    requestId?: string;
  };
}

interface LoggerOptions {
  maxSizeMB?: number;
  maxLogs?: number;
  enablePerformance?: boolean;
  enableMemory?: boolean;
  enableContext?: boolean;
  storageKey?: string;
}

class AdvancedLogger {
  private logs: LogEntry[] = [];
  private readonly searchIndex = new Map<string, Set<string>>();
  private readonly maxSizeBytes: number;
  private readonly maxLogs: number;
  private readonly options: LoggerOptions;
  private readonly sessionId: string;
  private readonly requestId: string;

  constructor(options: LoggerOptions = {}) {
    this.options = {
      maxSizeMB: 9,
      maxLogs: 10000,
      enablePerformance: true,
      enableMemory: true,
      enableContext: true,
      storageKey: 'advanced-logs',
      ...options,
    };

    this.maxSizeBytes = this.options.maxSizeMB! * 1024 * 1024;
    this.maxLogs = this.options.maxLogs!;
    this.sessionId = crypto.randomUUID();
    this.requestId = crypto.randomUUID();

    this.loadFromStorage();
    this.setupKeyboardShortcuts();
  }

  private generateSearchableText(
    message: string,
    data?: unknown,
    group?: string,
    tags?: string[]
  ): string {
    return [
      message,
      group,
      ...(tags || []),
      data ? JSON.stringify(data) : '',
      new Error().stack?.split('\n').slice(1, 4).join(' '),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  private addToSearchIndex(entry: LogEntry) {
    const words = entry.searchableText!.split(/\s+/);
    words.forEach(word => {
      if (word.length > 2) {
        if (!this.searchIndex.has(word)) {
          this.searchIndex.set(word, new Set());
        }
        this.searchIndex.get(word)!.add(entry.id);
      }
    });
  }

  private getMemoryUsage() {
    if (!this.options.enableMemory || !('memory' in performance)) {
      return undefined;
    }

    const memory = (performance as { memory: unknown }).memory as {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    return {
      used: memory.usedJSHeapSize,
      total: memory.totalJSHeapSize,
      limit: memory.jsHeapSizeLimit,
    };
  }

  private getPerformanceMetrics() {
    if (!this.options.enablePerformance) {
      return undefined;
    }

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    return {
      navigationStart: navigation?.startTime || 0,
      loadEventEnd: navigation?.loadEventEnd || 0,
      domContentLoaded: navigation?.domContentLoadedEventEnd || 0,
    };
  }

  private getContext() {
    if (!this.options.enableContext) {
      return undefined;
    }

    return {
      sessionId: this.sessionId,
      requestId: this.requestId,
      userId: this.getUserId(),
    };
  }

  private getUserId(): string | undefined {
    // Try to get userId from various sources
    const userFromLocalStorage = localStorage.getItem('userId');
    const userFromSessionStorage = sessionStorage.getItem('userId');
    const userFromCookie = document.cookie.match(/userId=([^;]+)/)?.[1];

    return userFromLocalStorage || userFromSessionStorage || userFromCookie;
  }

  log(level: LogEntry['level'], message: string, data?: unknown, group?: string, tags?: string[]) {
    const entry: LogEntry = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      datetime: new Date().toISOString(),
      level,
      message,
      data,
      stack: new Error().stack,
      group,
      tags,
      searchableText: this.generateSearchableText(message, data, group, tags),
      userAgent: navigator.userAgent,
      url: window.location.href,
      memoryUsage: this.getMemoryUsage(),
      performance: this.getPerformanceMetrics(),
      context: this.getContext(),
    };

    this.logs.push(entry);
    this.addToSearchIndex(entry);
    this.saveToStorage();
    this.trimLogs();
  }

  group(name: string, fn: () => void) {
    const startTime = Date.now();
    fn();
    const duration = Date.now() - startTime;

    const groupLogs = this.logs.filter(log => log.group === name);
    groupLogs.forEach(log => {
      log.duration = duration;
    });
  }

  private saveToStorage() {
    try {
      const data = JSON.stringify(this.logs);
      if (data.length > this.maxSizeBytes) {
        this.trimLogs();
      }
      localStorage.setItem(this.options.storageKey!, data);
    } catch (error) {
      console.warn('Failed to save logs to localStorage:', error);
    }
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.options.storageKey!);
      if (stored) {
        this.logs = JSON.parse(stored);
        this.rebuildSearchIndex();
      }
    } catch (error) {
      console.warn('Failed to load logs from localStorage:', error);
    }
  }

  private rebuildSearchIndex() {
    this.searchIndex.clear();
    this.logs.forEach(log => this.addToSearchIndex(log));
  }

  private trimLogs() {
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
      this.rebuildSearchIndex();
    }
  }

  search(options: {
    query?: string;
    level?: string;
    group?: string;
    tags?: string[];
    startTime?: number;
    endTime?: number;
    limit?: number;
  }): LogEntry[] {
    let results = this.logs;

    if (options.startTime) {
      results = results.filter(log => log.timestamp >= options.startTime!);
    }
    if (options.endTime) {
      results = results.filter(log => log.timestamp <= options.endTime!);
    }
    if (options.level) {
      results = results.filter(log => log.level === options.level);
    }
    if (options.group) {
      results = results.filter(log => log.group === options.group);
    }
    if (options.tags?.length) {
      results = results.filter(log => options.tags!.some(tag => log.tags?.includes(tag)));
    }
    if (options.query) {
      const queryWords = options.query.toLowerCase().split(/\s+/);
      results = results.filter(log => {
        return queryWords.every(word => log.searchableText!.includes(word));
      });
    }

    results.sort((a, b) => b.timestamp - a.timestamp);

    if (options.limit) {
      results = results.slice(0, options.limit);
    }

    return results;
  }

  getStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {} as Record<string, number>,
      byGroup: {} as Record<string, number>,
      byHour: {} as Record<number, number>,
      recentErrors: this.logs.filter(log => log.level === 'error').slice(-10),
      memoryUsage: this.getMemoryUsage(),
      storageSize: this.getStorageSize(),
    };

    this.logs.forEach(log => {
      stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1;
      if (log.group) {
        stats.byGroup[log.group] = (stats.byGroup[log.group] || 0) + 1;
      }
      const hour = new Date(log.timestamp).getHours();
      stats.byHour[hour] = (stats.byHour[hour] || 0) + 1;
    });

    return stats;
  }

  private getStorageSize(): number {
    try {
      const data = localStorage.getItem(this.options.storageKey!);
      return data ? new Blob([data]).size : 0;
    } catch {
      return 0;
    }
  }

  clear() {
    this.logs = [];
    this.searchIndex.clear();
    localStorage.removeItem(this.options.storageKey!);
  }

  // Methods for different logging levels
  debug(message: string, data?: unknown, group?: string, tags?: string[]) {
    this.log('debug', message, data, group, tags);
  }

  info(message: string, data?: unknown, group?: string, tags?: string[]) {
    this.log('info', message, data, group, tags);
  }

  warn(message: string, data?: unknown, group?: string, tags?: string[]) {
    this.log('warn', message, data, group, tags);
  }

  error(message: string, data?: unknown, group?: string, tags?: string[]) {
    this.log('error', message, data, group, tags);
  }

  fatal(message: string, data?: unknown, group?: string, tags?: string[]) {
    this.log('fatal', message, data, group, tags);
  }

  private setupKeyboardShortcuts() {
    document.addEventListener('keydown', e => {
      // Ctrl+Shift+L for exporting logs
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        this.exportLogs();
      }

      // Ctrl+Shift+X for clearing logs
      if (e.ctrlKey && e.shiftKey && e.key === 'X') {
        this.showClearConfirmation();
      }
    });
  }

  private showClearConfirmation() {
    const confirmed = window.confirm(
      'Are you sure you want to delete all logs?\n\n' + 'This action cannot be undone.'
    );

    if (confirmed) {
      this.clear();
      alert('All logs have been deleted.');
    }
  }

  private exportLogs() {
    const data = {
      timestamp: new Date().toISOString(),
      logs: this.logs,
      stats: this.getStats(),
      summary: {
        total: this.logs.length,
        byLevel: this.logs.reduce(
          (acc, log) => {
            acc[log.level] = (acc[log.level] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>
        ),
      },
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

export const logger = new AdvancedLogger();
export { AdvancedLogger };
export type { LogEntry, LoggerOptions };

export { keyboardManager, KeyboardShortcutManager } from './keyboard-shortcuts';
export type { KeyboardShortcut } from './keyboard-shortcuts';
